import { lazy, Suspense, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';

import { Ceremony, Spinner } from '@/shared/ui';

import { ErrorBoundary } from './ErrorBoundary';
import { PinGate } from './PinGate';
import { RiskGate } from './RiskGate';
import { AdminLayout } from './layout/AdminLayout';
import { AppLayout } from './layout/AppLayout';
import { RequireAuth, RequireStaff } from './RequireAuth';
import styles from './router.module.css';
import { TelegramBridge } from './TelegramBridge';

/**
 * Все экраны — lazy. В TMA важен первый кадр: бандл поля не должен тащить
 * админку, а админка — движок графиков клиентской статистики.
 *
 * Пути и файлы фиксированы контрактом: экраны пишут другие агенты.
 */
const FieldScreen = lazy(() => import('@/features/field/FieldScreen').then(m => ({ default: m.FieldScreen })));
const WatchScreen = lazy(() => import('@/features/field/WatchScreen').then(m => ({ default: m.WatchScreen })));
const BridgeScreen = lazy(() => import('@/features/bridge/BridgeScreen').then(m => ({ default: m.BridgeScreen })));
const GamesScreen = lazy(() => import('@/features/games/GamesScreen').then(m => ({ default: m.GamesScreen })));
const ProfileScreen = lazy(() => import('@/features/profile/ProfileScreen').then(m => ({ default: m.ProfileScreen })));
const DepositScreen = lazy(() => import('@/features/deposit/DepositScreen').then(m => ({ default: m.DepositScreen })));
const WithdrawScreen = lazy(() => import('@/features/withdraw/WithdrawScreen').then(m => ({ default: m.WithdrawScreen })));
const StatsScreen = lazy(() => import('@/features/stats/StatsScreen').then(m => ({ default: m.StatsScreen })));
const AchievementsScreen = lazy(() => import('@/features/achievements/AchievementsScreen').then(m => ({ default: m.AchievementsScreen })));
const ReferralsScreen = lazy(() => import('@/features/referrals/ReferralsScreen').then(m => ({ default: m.ReferralsScreen })));
const RiskAcceptanceScreen = lazy(() => import('@/features/risk-acceptance/RiskAcceptanceScreen').then(m => ({ default: m.RiskAcceptanceScreen })));
const FaqScreen = lazy(() => import('@/features/faq/FaqScreen').then(m => ({ default: m.FaqScreen })));
// AuthScreen экспортируется как default: обращение к m.AuthScreen давало
// undefined-компонент и /auth падал в ErrorBoundary.
const AuthScreen = lazy(() => import('@/features/auth/AuthScreen'));
const NotFoundScreen = lazy(() => import('@/features/NotFoundScreen').then(m => ({ default: m.NotFoundScreen })));

const AdminDashboardScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminDashboardScreen })));
const ClientsScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.ClientsScreen })));
const ClientDetailScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.ClientDetailScreen })));
const CoreScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.CoreScreen })));
const WithdrawalsScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.WithdrawalsScreen })));
const AdminDepositsScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.DepositsScreen })));
const ProcessScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.ProcessScreen })));
const ProfitCalculatorScreen = lazy(() => import('@/features/admin/ProfitCalculatorScreen').then(m => ({ default: m.ProfitCalculatorScreen })));
const RoundsHistoryScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.RoundsHistoryScreen })));
const BroadcastScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.BroadcastScreen })));
const FaqAdminScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.FaqAdminScreen })));
const AuditScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.AuditScreen })));
const AnalyticsScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.AnalyticsScreen })));
const ReferralsAdminScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.ReferralsAdminScreen })));
const AdjustmentsScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.AdjustmentsScreen })));
const SettingsScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.SettingsScreen })));
const StaffScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.StaffScreen })));
const LevelRequestsScreen = lazy(() => import('@/features/admin').then(m => ({ default: m.LevelRequestsScreen })));

function ScreenFallback(): ReactElement {
  return (
    <div className={styles.fallback}>
      <Spinner size={26} />
      <Ceremony tone="teal">Загрузка...</Ceremony>
    </div>
  );
}

/** Обёртка ленивого экрана: Suspense + локальный ErrorBoundary на маршрут. */
function Lazy({ component: Component }: { component: ComponentType }): ReactElement {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ScreenFallback />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  );
}

/** Корень: мост к Telegram монтируется один раз над всем деревом маршрутов. */
function Root(): ReactElement {
  return (
    <>
      <TelegramBridge />
      <Outlet />
    </>
  );
}

/**
 * Строгое разделение роутов:
 * - Public: /auth
 * - Client: / (без префикса, под RequireAuth)
 * - Admin: /admin (отдельная ветка, под RequireStaff)
 * - 404: catch-all на верхнем уровне
 */
const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      // Public routes
      { path: '/auth', element: <Lazy component={AuthScreen} /> },

      // До согласия с рисками защищаем только уже установленным PIN.
      {
        path: '/risk-acceptance',
        element: (
          <RequireAuth>
            <PinGate offerSetup={false}>
              <Lazy component={RiskAcceptanceScreen} />
            </PinGate>
          </RequireAuth>
        ),
      },

      // Client routes (без префикса)
      {
        element: (
          <RequireAuth>
            <RiskGate>
              <PinGate>
                <AppLayout />
              </PinGate>
            </RiskGate>
          </RequireAuth>
        ),
        children: [
          { path: '/', element: <Lazy component={FieldScreen} /> },
          { path: '/field/:roundId/watch', element: <Lazy component={WatchScreen} /> },
          { path: '/bridge', element: <Lazy component={BridgeScreen} /> },
          { path: '/games', element: <Lazy component={GamesScreen} /> },
          { path: '/profile', element: <Lazy component={ProfileScreen} /> },
          { path: '/deposit', element: <Lazy component={DepositScreen} /> },
          { path: '/withdraw', element: <Lazy component={WithdrawScreen} /> },
          { path: '/stats', element: <Lazy component={StatsScreen} /> },
          { path: '/achievements', element: <Lazy component={AchievementsScreen} /> },
          { path: '/referrals', element: <Lazy component={ReferralsScreen} /> },
          { path: '/faq', element: <Lazy component={FaqScreen} /> },
        ],
      },

      // Admin routes (отдельная ветка с префиксом /admin)
      {
        path: '/admin',
        element: (
          <RequireStaff>
            <RiskGate>
              <PinGate>
                <AdminLayout />
              </PinGate>
            </RiskGate>
          </RequireStaff>
        ),
        children: [
          { index: true, element: <Lazy component={AdminDashboardScreen} /> },
          { path: 'clients', element: <Lazy component={ClientsScreen} /> },
          { path: 'clients/:id', element: <Lazy component={ClientDetailScreen} /> },
          { path: 'core', element: <Lazy component={CoreScreen} /> },
          { path: 'withdrawals', element: <Lazy component={WithdrawalsScreen} /> },
          { path: 'deposits', element: <Lazy component={AdminDepositsScreen} /> },
          {
            path: 'process',
            element: <Lazy component={ProcessScreen} />,
            children: [
              { path: 'calculator', element: <Lazy component={ProfitCalculatorScreen} /> },
            ],
          },
          { path: 'rounds-history', element: <Lazy component={RoundsHistoryScreen} /> },
          { path: 'analytics', element: <Lazy component={AnalyticsScreen} /> },
          { path: 'referrals', element: <Lazy component={ReferralsAdminScreen} /> },
          { path: 'adjustments', element: <Lazy component={AdjustmentsScreen} /> },
          { path: 'level-requests', element: <Lazy component={LevelRequestsScreen} /> },
          { path: 'broadcast', element: <Lazy component={BroadcastScreen} /> },
          { path: 'faq', element: <Lazy component={FaqAdminScreen} /> },
          { path: 'audit', element: <Lazy component={AuditScreen} /> },
          { path: 'settings', element: <Lazy component={SettingsScreen} /> },
          { path: 'staff', element: <Lazy component={StaffScreen} /> },
        ],
      },

      // 404 catch-all на верхнем уровне
      { path: '*', element: <Lazy component={NotFoundScreen} /> },
    ],
  },
]);

export function AppRouter(): ReactElement {
  return <RouterProvider router={router} fallbackElement={<ScreenFallback />} />;
}
